import { Button, Input, Select } from "antd";
import { ColumnsType } from "antd/es/table";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import SearchIcon from "../../../assets/icons/SearchIcon";
import {
  addKeyInArray,
  ArrayOption,
  selectSearchOption,
} from "../../../helpers/siteConfig";
import useDebounce from "../../../hooks/useDebounce";
import useTable from "../../../hooks/useTable";
import {
  getAllAreaAsync,
  getAllRegionAsync,
} from "../../../store/features/NP/geoInfo/geoAPI";
import {
  addBatchTaskAsync,
  getCompletedTowerBySupplierAsync,
} from "../../../store/features/NP/supplier/supplierAPI";
import { useAppDispatch, useAppSelector } from "../../../store/store";
import {
  GetCompletedTowerBySupplierData,
  GetCompletedTowerBySupplierParams,
} from "../../../types/redux/NP/supplier";
import { capitalize } from "../../../utils/string";
import CustomModal from "../../common/CustomModal";
import CustomTable from "../../common/CustomTable";

type Props = {
  open: boolean;
  handleClose: (val?: boolean) => void;
  data?: any;
};
const AddTaskModal = ({ open, handleClose, data }: Props) => {
  const { page, handlePagination, limit } = useTable();
  const { handleInputChange, keyword } = useDebounce();
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const [selectedData, setSelectedData] = useState<string[]>([]);
  const [regionId, setRegionId] = useState<string | undefined>(undefined);
  const [areaId, setAreaId] = useState<string | undefined>(undefined);
  const { regions, areas } = useAppSelector((state) => state.npGeo);
  const {
    loading,
    postLoading,
    allCompletedTowerBySupplier,
    completedTowerPaginationResponse,
  } = useAppSelector((state) => state.supplier);
  const { login } = useAppSelector((state) => state.login);
  const prevData = data?.map((el: any) => el?._id) || [];

  const handleAdd = () => {
    if (!selectedData?.length) return;

    dispatch(
      addBatchTaskAsync({
        data: {
          towerTicketLogArray: [...selectedData, ...prevData],
        },
        others: {
          id,
          reRender: handleClose,
        },
      })
    );
  };

  const columns: ColumnsType<GetCompletedTowerBySupplierData> = [
    {
      title: "Cluster",
      dataIndex: "tower",
      align: "center",
      render: (val) => capitalize(val?.regionName),
    },
    {
      title: "Zonal",
      dataIndex: "tower",
      align: "center",
      render: (val) => capitalize(val?.areaName),
    },
    {
      title: "Site ID",
      dataIndex: "tower",
      align: "center",
      render: (val) => capitalize(val?.siteCode),
    },
    {
      title: "Tower",
      dataIndex: "tower",
      align: "center",
      render: (val) => capitalize(val?.towerName) || "N/A",
    },
    {
      title: "PG Name",
      dataIndex: "pg",
      render: (val) =>
        `${val?.firstName} ${val?.lastName ? val?.lastName : ""}`,
    },
    {
      title: "Missed Call",
      dataIndex: "missCall",
      align: "center",
      render: (val: boolean) => (val ? "Yes" : "No"),
    },
    {
      title: "Base Amount",
      dataIndex: "totalAmount",
      align: "center",
      render: (val) => val || "N/A",
    },
    {
      title: "PGRH",
      dataIndex: "totalValidRunTime",
      align: "center",
      render: (val) => val || "N/A",
    },
    // {
    //   title: "PGRH Rate",
    //   dataIndex: "PGRH Rate",
    //   align: "center",
    //   render: (val: boolean) => "N/A",
    // },
    // {
    //   title: "Total Amount",
    //   dataIndex: "totalAmount",
    //   align: "center" as const,
    //   render: (val: any) => `৳ ${val ? val : 0}`,
    // },
    // {
    //   title: "Working Hrs",
    //   dataIndex: "totalValidRunTime",
    //   align: "center",
    //   render: (val) => Math.round(val || 0) + " min",
    // },
  ];

  const getCompletedTowerBySupplierData = useCallback(() => {
    const params: GetCompletedTowerBySupplierParams = {
      cluster: regionId,
      area: areaId,
      page: page,
      limit: limit,
      siteCode: keyword,
      suppler: login?.suppler,
    };

    if (login?.role === "vendor" || login?.role === "region") {
      params.cluster = login?.region;
    }

    dispatch(
      getCompletedTowerBySupplierAsync({
        params: params,
      })
    );
  }, [
    areaId,
    dispatch,
    keyword,
    limit,
    login?.region,
    login?.role,
    login?.suppler,
    page,
    regionId,
  ]);

  useEffect(() => {
    getCompletedTowerBySupplierData();
  }, [getCompletedTowerBySupplierData]);

  useEffect(() => {
    dispatch(getAllRegionAsync({}));
  }, [dispatch]);

  useEffect(() => {
    if (regionId)
      dispatch(
        getAllAreaAsync({
          params: {
            region: regionId,
          },
        })
      );
  }, [dispatch, regionId]);

  useEffect(() => {
    if (login?.role === "vendor" || login?.role === "region") {
      setRegionId(login?.region);
    }
  }, [dispatch, login?.region, login?.role]);

  return (
    <CustomModal
      width={1200}
      title="Add task to batch/voucher"
      open={open}
      handleClose={handleClose}
    >
      <div className="px-6">
        <CustomTable
          loading={loading}
          total={completedTowerPaginationResponse?.total || 0}
          page={completedTowerPaginationResponse?.page || 1}
          limit={completedTowerPaginationResponse?.limit || 10}
          handlePagination={handlePagination}
          columns={columns}
          dataList={addKeyInArray(allCompletedTowerBySupplier || [])}
          hideShadow
          rowSelection={{
            type: "checkbox",
            selectedRowKeys: selectedData,
            onChange: (key) => {
              setSelectedData(key as string[]);
            },
            columnWidth: 40,
          }}
          topOfTheTableComponent={
            <div className="flex justify-end items-center gap-4 flex-wrap p-5">
              <Select
                value={regionId}
                placeholder="Cluster"
                allowClear
                showSearch
                filterOption={selectSearchOption}
                options={ArrayOption(regions, "regionName", "_id", true)}
                onChange={(val) => {
                  setRegionId(val);
                  setAreaId(undefined);
                }}
                className="min-w-[120px]"
                disabled={login?.role === "vendor"}
                size="large"
              />
              <Select
                placeholder="Zone"
                allowClear
                showSearch
                filterOption={selectSearchOption}
                options={ArrayOption(areas, "areaName", "_id", true)}
                onChange={(val) => {
                  setAreaId(val);
                }}
                value={areaId}
                disabled={!regionId}
                className="min-w-[120px]"
                size="large"
              />
              <div className="flex items-center gap-4">
                <span className="text-sm text-body font-medium">Search:</span>
                <Input
                  placeholder="Search by site id"
                  size="middle"
                  onChange={handleInputChange}
                  suffix={
                    <span className="text-accent block">
                      <SearchIcon />
                    </span>
                  }
                />
              </div>
            </div>
          }
          underThePaginationComponent={
            <div className="p-4 mt-2.5 flex items-center justify-end gap-6 flex-wrap">
              <Button
                onClick={() => handleClose()}
                type="default"
                size="large"
                className="w-36 !bg-accent !text-white hover:bg-accent hover:text-white border-0 hover:border-0"
              >
                Cancel
              </Button>
              {!!allCompletedTowerBySupplier?.length && (
                <Button
                  type="primary"
                  size="large"
                  className="w-36"
                  disabled={!selectedData?.length}
                  onClick={handleAdd}
                  loading={postLoading}
                >
                  Add
                </Button>
              )}
            </div>
          }
        />
      </div>
    </CustomModal>
  );
};

export default AddTaskModal;
